<template>
    <form
        id="KidsForm"
        class="flex flex-col gap-4 h-64"
        @submit.prevent="form.post(action)"
    >
        <div class="flex items-center gap-4">
            <Tag
                wrapper-class="w-20"
                interactive
                :selected="form.has_kids === true"
                :mark-selected="false"
                data-test="has_kids_true"
                @click="form.has_kids = true"
                >{{ $t("generic.lms_yes") }}</Tag
            >
            <Tag
                wrapper-class="w-20"
                interactive
                :selected="form.has_kids === false"
                :mark-selected="false"
                data-test="has_kids_false"
                @click="form.has_kids = false"
                >{{ $t("generic.lms_no") }}</Tag
            >
        </div>
        <transition
            enter-active-class="transition duration-300 ease-out"
            enter-from-class="translate-y-1 opacity-0"
            enter-to-class="translate-y-0 opacity-100"
            leave-active-class="transition duration-150 ease-in"
            leave-from-class="translate-y-0 opacity-100"
            leave-to-class="translate-y-1 opacity-0"
        >
            <div v-if="form.has_kids">
                <SelectField
                    v-model="form.children_number"
                    name="children_number"
                    :label="$t('home.onboarding.fields.children_number')"
                    :options="childrenOptions"
                    :error="translateMaybe(form.errors.children_number)"
                    class="mb-6"
                    setup
                    custom-placeholder-class="text-gray-700"
                />
                <SelectField
                    v-model="form.children_number_in_household"
                    name="children_in_household"
                    :label="
                        $t(
                            'home.onboarding.fields.children_number_in_household'
                        )
                    "
                    :options="childrenInHouseholdOptions"
                    :error="
                        translateMaybe(form.errors.children_number_in_household)
                    "
                    setup
                    custom-text-class="w-56 md:w-full"
                    custom-placeholder-class="text-gray-700"
                />
            </div>
        </transition>
    </form>
</template>

<script setup>
import SelectField from "@/Components/FormFields/SelectField.vue";
import Tag from "@/Components/Tags/Tag.vue";
import { OPTIONS } from "@/Helpers/constants.js";
import { translateMaybe } from "@/Helpers/translateMaybe.js";
import { useForm } from "@inertiajs/vue3";
import { range } from "lodash";
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

const { t } = useI18n({ useScope: "global" });
const props = defineProps({
    action: {
        type: String,
        default: null,
    },
    form: {
        type: Object,
        default: null,
    },
});

const form =
    props.form ??
    useForm({
        has_kids: false,
        children_number: 0,
        children_number_in_household: 0,
    });

const childrenInHouseholdRange = ref(
    form.children_number ? form.children_number + 1 : 2
);

const childrenOptions = computed(() => OPTIONS.CHILDREN.slice(1));
const childrenInHouseholdOptions = computed(() =>
    range(0, childrenInHouseholdRange.value).map((value) => ({
        value,
        text: t(`generic.kids_in_house${value === 6 ? "_more" : ""}`, {
            count: value,
        }),
    }))
);

watch(
    () => form.has_kids,
    (newValue) => {
        form.children_number = Number(newValue);
        form.children_number_in_household = Number(newValue);
    }
);

watch(
    () => form.children_number,
    (newValue) => {
        childrenInHouseholdRange.value = newValue + 1;
    }
);
</script>
